import React, { useState, useEffect, useContext } from 'react';
import RequestService from '../../services/RequestService';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import {
    Grid2 as Grid,
    Typography,
    Breadcrumbs,
    Link,
    Button,
    Box,
    Snackbar,
    Alert,
    useMediaQuery,
    TablePagination,
    Stack,
    Chip,
} from '@mui/material';
import { UserContext } from '../../contexts/UserContext';
import { useTheme } from '@mui/material/styles';
import TableComponent from '../../widgets/TableComponent';
import NewQuizDialog from '../../widgets/quiz/NewQuizDialog';
import EditQuizDialog from '../../widgets/quiz/EditQuizDialog';
import DeleteQuizDialog from '../../widgets/quiz/DeleteQuizDialog';
import QuizList from '../../widgets/quiz/QuizList';

const QuizPage = () => {
    const { user, hasPermission } = useContext(UserContext);
    const [quizzes, setQuizzes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalElements, setTotalElements] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [selectedQuiz, setSelectedQuiz] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchChallangers = async () => {
        try {
            const data = await RequestService.getQuizzes(user.company.id, {
                page,
                size: rowsPerPage,
            });
            setQuizzes(data.quizzes || []);
            setLoading(false);
        } catch (err) {
            setError('Erro ao carregar grupos.');
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchChallangers();
    }, [page, rowsPerPage]);

    const handleRowClick = (quiz) => {
        if (hasPermission('QUIZ_UPDATE')) {
            setSelectedQuiz(quiz);
            setIsDialogOpen(true);
        }
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setSelectedQuiz(null);
    };

    const handleOpenAddDialog = () => setIsAddDialogOpen(true);
    const handleCloseAddDialog = () => setIsAddDialogOpen(false);

    const handleEdit = (challanger) => {
        handleRowClick(challanger);
    };

    const handleOpenDeleteDialog = (challanger) => {
        setSelectedQuiz(challanger);
        setIsDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setIsDeleteDialogOpen(false);
        setSelectedQuiz(null);
    };

    const handleSuccessDelete = async () => {
        await fetchChallangers();
        setSnackbarMessage('Jogo excluído com sucesso!');
        setSnackbarOpen(true);
    };

    const handleSuccessAdd = async (message) => {
        await fetchChallangers();
        setSnackbarMessage(message);
        setSnackbarOpen(true);
    };

    const handleSuccessEdit = async (message) => {
        await fetchChallangers();
        setSnackbarMessage(message);
        setSnackbarOpen(true);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const columns = [
        {
            label: 'Título e Descrição',
            field: 'title',
            align: 'left',
            renderCell: (value, row) => (
                <Stack direction='column' spacing={1}>
                    <Typography variant='body2' sx={{ color: '#292929' }}>
                        {row.title}
                    </Typography>
                </Stack>
            ),
        },
        {
            label: 'Descrição',
            field: 'description',
            align: 'center',
            renderCell: (value, row) => (
                <Typography
                    variant='body2'
                    sx={{ fontSize: '14px', color: '#292929' }}
                >
                    {row.description}
                </Typography>
            ),
        },
        {
            label: '',
            field: 'actions',
            align: 'right',
            renderCell: (value, row) => (
                <Stack direction='row' spacing={1}>
                    <Button onClick={() => handleEdit(row)} size='small'>
                        Editar
                    </Button>
                    <Button
                        onClick={() => handleOpenDeleteDialog(row)}
                        size='small'
                        color='error'
                    >
                        Excluir
                    </Button>
                </Stack>
            ),
        },
    ];

    const ChipGroup = ({ items, labelKey }) => {
        if (!items || items.length === 0) return null;

        const visibleItems = items.slice(0, 2);
        const remainingCount = items.length - 2;

        return (
            <Stack direction='row' spacing={1}>
                {visibleItems.map((item) => (
                    <Chip
                        key={item.id}
                        label={item[labelKey]}
                        size='small'
                        color='primary'
                    />
                ))}
                {remainingCount > 0 && (
                    <Chip
                        label={`+${remainingCount}`}
                        size='small'
                        color='default'
                    />
                )}
            </Stack>
        );
    };

    if (loading) return <div>Carregando...</div>;
    if (error) return <div>{error}</div>;

    const renderEmptyState = () => (
        <Box
            sx={{
                textAlign: 'center',
                mt: 4,
                color: '#666',
                fontFamily: 'Poppins',
                fontSize: 18,
            }}
        >
            <Typography variant='h6'>
                Nenhum jogo desafio cadastrado.
            </Typography>
            <Typography variant='body2'>
                Clique em <strong>"Novo desafio"</strong> para adicionar o
                primeiro!
            </Typography>
        </Box>
    );

    const renderMobileLayout = () => {
        return quizzes.length === 0 ? (
            renderEmptyState()
        ) : (
            <QuizList
                quizzes={quizzes}
                shouldEdit={hasPermission('QUIZ_UPDATE')}
                shouldDelete={hasPermission('QUIZ_DELETE')}
                onUserClick={handleRowClick}
                onDeleteClick={handleOpenDeleteDialog}
            />
        );
    };

    const renderDesktopLayout = () => {
        if (quizzes.length === 0) {
            return renderEmptyState();
        }

        return (
            <Box>
                <TableComponent columns={columns} rows={quizzes} />

                <TablePagination
                    component='div'
                    count={totalElements}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 20, 50]}
                />
            </Box>
        );
    };

    return (
        <Box>
            <Grid container justifyContent='start'>
                <Breadcrumbs aria-label='breadcrumb'>
                    <Link underline='hover' color='inherit' href='/'>
                        Início
                    </Link>
                    <Link underline='hover' color='inherit'>
                        Quiz
                    </Link>
                    <Typography sx={{ color: 'text.primary' }}>
                        Gerenciar
                    </Typography>
                </Breadcrumbs>
            </Grid>

            <Grid container xs={12} sx={{ mt: 3 }}>
                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontSize: 24,
                        fontWeight: '500',
                        color: '#0C3466',
                        mb: 2,
                    }}
                >
                    Quiz
                </Typography>
            </Grid>

            <Grid container sx={12} justifyContent={'end'}>
                <Grid item sx={6}>
                    {hasPermission('QUIZ_CREATE') && (
                        <Button
                            variant='contained'
                            sx={{ minHeight: '47px', mb: 2 }}
                            endIcon={<AddCircleOutlineOutlinedIcon />}
                            onClick={handleOpenAddDialog}
                        >
                            Novo Quiz
                        </Button>
                    )}
                </Grid>
            </Grid>

            {isMobile ? renderMobileLayout() : renderDesktopLayout()}

            <NewQuizDialog
                open={isAddDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseAddDialog();
                    }
                }}
                onSuccess={handleSuccessAdd}
                user={user}
            />

            <EditQuizDialog
                open={isDialogOpen}
                onClose={handleCloseDialog}
                onSuccess={handleSuccessEdit}
                user={user}
                quizId={selectedQuiz?.id}
            />

            <DeleteQuizDialog
                open={isDeleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                onConfirm={async () => {
                    try {
                        await RequestService.deleteQuizById(selectedQuiz.id);
                        handleCloseDeleteDialog();
                        await handleSuccessDelete();
                    } catch (error) {
                        console.error('Erro ao excluir quiz:', error);
                        setSnackbarMessage('Erro ao excluir quiz.');
                        setSnackbarOpen(true);
                    }
                }}
                quiz={selectedQuiz}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity='success'
                    variant='filled'
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default QuizPage;
